import React, { useEffect, useRef, useState } from 'react';
import { WalletCards, Syringe, SquarePen, X, Save, Loader, Plus } from 'lucide-react';
import { can_visit, notify, numberWithCommas } from "../../../helpers";
import { Config } from '../../../Config';
import { maximizingPreventiveCare, updateMaximizingPreventiveCare } from '../../../services/audits';
import { Drawer } from 'vaul';
import Spacing from '../../Spacing';
import Card from '../../Card';
import Grid from '../../Grid';

export default function MaximizingPreventiveCareModal({
  account_id,
  location_id,
  activeModal = false,
  setActiveModal = () => {}
}) {
  
  const currentDate = new Date();
  const [loadData, setLoadData] = useState(true);
  const [maximizingCareData, setMaximizingCareData] = useState([]);
  const [editMaximizingCare, setEditMaximizingCare] = useState(false);
  const [saveMaximizingCareLoader, setSaveMaximizingCareLoader] = useState(false);
  const [formData, setFormData] = useState({
    account_id: account_id,
    location_id: location_id,
    potential_revenue: 0,
    every_encounter_amount: 0,
    practice_opportunities: []
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    getMaximizingCareData(account_id, location_id);
  }, [account_id, location_id]);

  const getMaximizingCareData = async (accountId, locationId) => {
    try {
      const response = await maximizingPreventiveCare(accountId, locationId);

      if (response) {
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setMaximizingCareData(response?.data);
          setMaximizingFormData(response?.data);
          setLoadData(false);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getMaximizingCareData ~ error:", error);
    }
  };

  const setMaximizingFormData = (data) => {
    setFormData({
      account_id: account_id,
      location_id: location_id,
      ...data,
      practice_opportunities: data.practice_opportunities?.map(maximizingCare => ({
        ...maximizingCare
      }))
    });
  };

  const handleAddDataSource = () => {
    const newMaximizingCare = {
      year: currentDate.getFullYear(),
      annual_wellness_completed_visit: 0,
      annual_wellness_completed_visit_percentage: 0,
      potential_annual_wellness_completed_visit: 0
    };

    setFormData((prevData) => ({
      ...prevData,
      practice_opportunities: [...prevData.practice_opportunities, newMaximizingCare]
    }));
  };

  const handleDeleteRecord = async (index) => {
    setFormData((prevData) => {
      const updatedMaximizingCare = prevData.practice_opportunities.filter((_, i) => i !== index);
      return {
        ...prevData,
        practice_opportunities: updatedMaximizingCare
      };
    });
  };

  const handleInputChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleMaximizingCareChange = (index, key, value) => {
    const updatedMaximizingCare = formData.practice_opportunities.map((opp, i) => 
      i === index ? { ...opp, [key]: value } : opp
    );

    setFormData((prevData) => ({
      ...prevData,
      practice_opportunities: updatedMaximizingCare
    }));
  };

  const handleCancelEdit = () => {
    setMaximizingFormData(maximizingCareData);
    setEditMaximizingCare(false);
  };

  const updateMaximizingCareReport = async (e) => {
    e.preventDefault();
    setSaveMaximizingCareLoader(true);

    try {
      const response = await updateMaximizingPreventiveCare(formData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === 'success') {
            notify("success", response?.data?.message);
            await getMaximizingCareData(account_id, location_id);
            setEditMaximizingCare(false);
          } else  {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setSaveMaximizingCareLoader(false);
    }
  }

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={setActiveModal}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Patient Details</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">

              <div className="docpro_modal" style={{ backgroundColor: 'rgb(249, 249, 251)' }}>
                {loadData ? (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <>
                    <form onSubmit={updateMaximizingCareReport}>
                      <div className="docpro_page_heading_wrap audit_modal_heading">
                        <h2 className="docpro_page_heading docpro_fs_24">
                          <i
                            className="docpro_white_color"
                            style={{ backgroundColor: "#8a73ed" }}
                          >
                            <Syringe width={18} height={18} />
                          </i>
                          Maximizing Preventive Care
                        </h2>
                        <div className="docpro_page_heading_btns">
                          {can_visit('administrator') && (
                          !editMaximizingCare ?
                            <button 
                              type="button"
                              className="docpro_bordered_btn" 
                              style={{ background: '#1578d5', borderRadius: '5px', color: '#ffffff' }}
                              onClick={()=>{setEditMaximizingCare(true);}}
                            >
                              <SquarePen width={20} height={20} />
                              <span>Edit</span>
                            </button>
                          :
                            <div className="flex item-center docpro_grid_gap_10">
                              <button 
                                type="button"
                                className="docpro_bordered_btn" 
                                style={{ background: '#e5484d', borderRadius: '5px', color: '#ffffff' }}
                                onClick={handleCancelEdit}
                              >
                                <X width={20} height={20} />
                                <span>Cancel</span>
                              </button>
                              {editMaximizingCare && (
                                <button
                                  type="submit"
                                  style={{ borderRadius: '4px' }}
                                  className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                                  disabled={saveMaximizingCareLoader ? true : false}
                                >
                                  <Save width={18} height={18} />
                                  <span>Save</span>
                                  {saveMaximizingCareLoader && (
                                    <span className="docpro_loading">
                                      <Loader width={20} height={20} />
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <Grid variant="docpro_grid_gap_20 docpro_grid_col_3">
                        <div className="docpro_card docpro_card_md docpro_card_1">
                          <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
                            <WalletCards /> 
                            Potential Revenue
                          </h3>
                          <Spacing lg="5" sm="5" />
                          <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
                            {!editMaximizingCare ? 
                              <>${numberWithCommas(maximizingCareData?.potential_revenue? numberWithCommas(maximizingCareData?.potential_revenue) : 0.00)}</>
                            :
                              <span className="docpro_signout_input items-center">
                                $
                                <input
                                  type="text"
                                  className="docpro_fw_500"
                                  style={{ maxWidth: "160px" }}
                                  defaultValue={formData?.potential_revenue || 0}
                                  onChange={(e) => handleInputChange("potential_revenue", parseFloat(e.target.value))}
                                />
                              </span>
                            }
                          </p>
                        </div>
                      </Grid>
                      <Spacing lg="18" sm="18" />
                      <Card variant="docpro_card docpro_card_1">
                        <div className='flex items-center'>
                          <img src="/images/favicon.png" width={45} alt="Icon" className='mr-4' />
                          <ul className='docpro_mb_0 docpro_fs_16'>
                            <li>{Config?._AppName} provides the list of unique eligible patients for Annual wellness visit.</li>
                            <Spacing lg="5" sm="5" />
                            <li>Using {Config?._AppName}'s Pre-visit planning, automated texting and call features, your care manager can easily schedule these patients.</li>
                          </ul>
                        </div>
                      </Card>
                      <Spacing lg="22" sm="22" />
                      <div>
                        <h3 className='docpro_fs_20 docpro_mb_0 mb-1'>Practice opportunities</h3>
                        <p className='docpro_fs_14 docpro_mb_0 docpro_olive_color flex items-center'>Considering $
                          {!editMaximizingCare ? 
                            <>{maximizingCareData?.every_encounter_amount ? maximizingCareData?.every_encounter_amount : 0}</>
                          :
                            <span className="docpro_signout_input">
                              <input
                                type="text"
                                className="docpro_fw_500"
                                style={{ maxWidth: "40px", backgroundColor: "transparent" }}
                                name="every_encounter_amount"
                                defaultValue={formData?.every_encounter_amount || 0}
                                onChange={(e) => handleInputChange("every_encounter_amount", parseFloat(e.target.value))}
                              />
                            </span>
                          }
                          &nbsp;for every encounter</p>
                      </div>
                      <Spacing lg="18" sm="18" />

                      <div className="docpro_data_table_wrap">
                        <div className="docpro_table_responsive" style={{ minHeight: "100px", paddingTop: "6px" }}>
                          <table className="docpro_mb_0 opportunity_table" style={{ height: "100%" }}>
                            <thead>
                              <tr>
                                {editMaximizingCare && (
                                  <th style={{ width: '60px' }}></th>
                                )}
                                <th>Year</th>
                                <th>Annual Wellness Visit (Completed)</th>
                                <th>Your Revenue</th>
                                <th>Potential Annual Wellness Visit</th>
                                <th>Potential Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {!loadData ? (
                                formData?.practice_opportunities?.length > 0 ? (
                                  formData?.practice_opportunities?.map((item, index) => (
                                    <tr key={index}>
                                      {editMaximizingCare && (
                                        <td style={{ padding: 0 }}>
                                          <div className='flex items-center' style={{ justifyContent: "end" }}>
                                            {(index === formData?.practice_opportunities?.length - 1) ? (
                                              <div className="text-right">
                                                <button 
                                                  type="button"
                                                  onClick={handleAddDataSource} 
                                                  className="docpro_icon_btn docpro_fw_600 docpro_primary_color mr-1" 
                                                  style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                >
                                                  <Plus width={16} height={16} />
                                                  <span className="docpro_tip">Add</span>
                                                </button>
                                              </div>
                                            ) : (
                                              <div style={{ minWidth: '35px' }}></div>
                                            )}
                                            {formData?.practice_opportunities?.length > 1 && (
                                              <div className="text-right">
                                                <button
                                                  type="button"
                                                  onClick={() => handleDeleteRecord(index)}
                                                  className="docpro_icon_btn docpro_red_color docpro_icon_btn_red "
                                                  style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                >
                                                  <X width={16} height={16} />
                                                  <span className="docpro_tip">Remove</span>
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                      <td>
                                        {!editMaximizingCare ? 
                                          <>{item?.year || ""}</>
                                        :
                                          <span className="docpro_signout_input">
                                            <input
                                              type="text"
                                              className="docpro_fw_500"
                                              style={{ maxWidth: "50px" }}
                                              value={formData?.practice_opportunities[index]?.year || ""}
                                              onChange={(e) => handleMaximizingCareChange(index, "year", parseInt(e.target.value))}
                                            />
                                          </span>
                                        }
                                      </td>
                                      <td>
                                        <p className='docpro_mb_0 docpro_fs_16'>
                                          {!editMaximizingCare ? 
                                            <>{item?.annual_wellness_completed_visit ? numberWithCommas(item?.annual_wellness_completed_visit) : 0}</>
                                          :
                                            <span className="docpro_signout_input">
                                              <input
                                                type="text"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "50px" }}
                                                value={formData?.practice_opportunities[index]?.annual_wellness_completed_visit || 0}
                                                onChange={(e) => handleMaximizingCareChange(index, "annual_wellness_completed_visit", parseInt(e.target.value))}
                                              />
                                            </span>
                                          }
                                        </p>
                                        <Spacing lg="5" sm="5" />
                                        <div className="docpro_audit_progress maximizing_progress mb-2">
                                          <div 
                                            className="docpro_audit_progress_in docpro_audit_progress_in_3 maximizing_progress_3 docpro_fw_600 docpro_fs_14 docpro_primary_color"
                                            style={{ width: `${
                                              item?.annual_wellness_completed_visit_percentage
                                                ? item.annual_wellness_completed_visit_percentage
                                                : 0
                                              }%` }}
                                            >
                                            <span>
                                              {!editMaximizingCare ? 
                                                <>{item?.annual_wellness_completed_visit_percentage ? item?.annual_wellness_completed_visit_percentage : 0}%</>
                                              :
                                                <span className="docpro_signout_input">
                                                  <input
                                                    type="text"
                                                    className="docpro_fw_500"
                                                    style={{ maxWidth: "40px", minWidth: "40px", backgroundColor: "transparent", color: "#FFFFFF" }}
                                                    value={formData?.practice_opportunities[index]?.annual_wellness_completed_visit_percentage || 0}
                                                    onChange={(e) => handleMaximizingCareChange(index, "annual_wellness_completed_visit_percentage", parseFloat(e.target.value))}
                                                  />%
                                                </span>
                                              }
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        {!editMaximizingCare ? 
                                          <>${numberWithCommas((item?.annual_wellness_completed_visit || 0) * (maximizingCareData?.every_encounter_amount || 0))}</>
                                        :
                                          <>${numberWithCommas((formData?.practice_opportunities[index]?.annual_wellness_completed_visit || 0) * (maximizingCareData?.every_encounter_amount || 0))}</>
                                        }
                                      </td>
                                      <td>
                                        {!editMaximizingCare ? 
                                          <>{item?.potential_annual_wellness_completed_visit ? numberWithCommas(item?.potential_annual_wellness_completed_visit) : 0}</>
                                        :
                                          <span className="docpro_signout_input">
                                            <input
                                              type="text"
                                              className="docpro_fw_500"
                                              style={{ maxWidth: "50px" }}
                                              value={formData?.practice_opportunities[index]?.potential_annual_wellness_completed_visit || 0}
                                              onChange={(e) => handleMaximizingCareChange(index, "potential_annual_wellness_completed_visit", parseInt(e.target.value))}
                                            />
                                          </span>
                                        }
                                      </td>
                                      <td>
                                        {!editMaximizingCare ? 
                                          <>${numberWithCommas((item?.potential_annual_wellness_completed_visit || 0) * (maximizingCareData?.every_encounter_amount || 0))}</>
                                        :
                                          <>${numberWithCommas((formData?.practice_opportunities[index]?.potential_annual_wellness_completed_visit || 0) * (maximizingCareData?.every_encounter_amount || 0))}</>
                                        }
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td className="no_data" colSpan="5">
                                      No data available
                                    </td>
                                  </tr>
                                )
                              ) : (
                                <tr>
                                  <td className="no_data" colSpan="5">
                                    Loading...
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}